import React, { createContext, useCallback, useContext, useRef, useState } from 'react';
import SplashScreen from '../components/SplashScreen';

// Splash controller. The full-page "signals converging" animation is shown ONLY at
// deliberate moments — a brand-new visitor's first arrival, login→enter, and tile-click
// loading — NEVER on a plain refresh.
//
// playSplash(onReveal, durationMs, opts?) plays it once:
//   • onReveal fires UNDER COVER (mid-splash, backdrop still opaque) — use it to navigate,
//     so the destination mounts behind the splash and the crossfade reveals the NEW page
//     (no "old page flashes first" glitch).
//   • opts.tagline  — a bespoke caption for this transition (e.g. Research OS).
//   • opts.spinner  — show the Maghaweer radar wheel instead of the ambient signal field.
const SplashCtx = createContext({ playSplash: () => {} });
export const useSplash = () => useContext(SplashCtx);

export function SplashProvider({ children }) {
  const [active, setActive] = useState(false);
  const [duration, setDuration] = useState(4200);
  const [opts, setOpts] = useState({});
  const cbRef = useRef(null);

  const playSplash = useCallback((onReveal = null, durationMs = 4200, options = {}) => {
    cbRef.current = onReveal;
    setDuration(durationMs);
    setOpts(options || {});
    setActive(true);
  }, []);

  // Navigate under cover (fired ~mid-splash). Idempotent — the callback is nulled after use.
  const reveal = useCallback(() => {
    const cb = cbRef.current; cbRef.current = null;
    if (cb) cb();
  }, []);

  // Tear the splash down (fired at animation end / on skip).
  const finish = useCallback(() => { setActive(false); }, []);

  return (
    <SplashCtx.Provider value={{ playSplash }}>
      {children}
      {active && (
        <SplashScreen
          onReveal={reveal}
          onDone={finish}
          durationMs={duration}
          tagline={opts.tagline || ''}
          spinner={!!opts.spinner}
        />
      )}
    </SplashCtx.Provider>
  );
}
